import React, { useState } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import {
  Button, Dialog, DialogTitle, DialogContent, TextField, DialogActions, Typography, Snackbar,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { withStyles } from '@material-ui/core/styles';
import { DatePicker } from '@material-ui/pickers';
import { gql, useMutation } from '@apollo/client';
import getHelpfulErrorMessage from 'utils/getHelpfulErrorMessage';

const EXTEND_INVOICE = gql`
  mutation ExtendInvoice($input: ExtendInvoiceLockDateInput!) {
    extendInvoiceLockDate(input: $input)
  }
`;

const ExtendButton = ({
  accountId,
  invoice,
  onExtend,
  classes,
}) => {

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [extendDate, setExtendDate] = useState(null);
  const [isExtending, setIsExtending] = useState(false);
  const [hasExtended, setHasExtended] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState(null);

  const [extendInvoiceLockDate] = useMutation(EXTEND_INVOICE);

  const handleOpenDialog = () => {
    setIsDialogOpen(true);
    setReason('');
    setExtendDate(moment(invoice.lock_date).add(7, 'day').toDate());
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
  };

  const handleExtendDateChange = (date) => {
    setExtendDate(date.toDate());
  };

  const handleReasonChange = (event) => {
    setReason(event.target.value);
  };

  const handleExtendClick = async () => {

    try {

      setIsExtending(true);

      await extendInvoiceLockDate({
        variables: {
          input: {
            invoice_id: invoice.id,
            account_id: accountId,
            date: extendDate,
            notes: reason,
          },
        },
      });

      // Reset state
      setIsDialogOpen(false);
      setIsExtending(false);
      setReason('');
      setExtendDate(null);
      setHasExtended(true);

      // Tell parent to reload the invoice list w/ updated date
      onExtend();

    } catch (err) {
      setError(getHelpfulErrorMessage(err));
      setIsExtending(false);
    }

  };

  const handleCloseSnackbar = () => {
    setHasExtended(false);
  };

  const isValid = reason.length > 0 && extendDate;

  return (
    <>
      <Button className={classes.extendButton} onClick={handleOpenDialog}>Extend</Button>
      {isDialogOpen && (
        <Dialog open={isDialogOpen} onClose={handleCloseDialog} fullWidth>
          <DialogTitle>Extend Invoice</DialogTitle>
          <DialogContent>

            {error && (
              <Alert severity="error">
                {error}
              </Alert>
            )}

            <Typography variant="body1">
              When would you like to extend this invoice lock date to?
            </Typography>

            <DatePicker
              clearable
              disabled={isExtending}
              value={extendDate}
              onChange={handleExtendDateChange}
              minDate={new Date()}
              fullWidth
              margin="normal"
              inputVariant="outlined"
              format="Do MMMM, YYYY"
            />

            <TextField
              disabled={isExtending}
              value={reason}
              onChange={handleReasonChange}
              label="Reason for extending"
              variant="outlined"
              fullWidth
              margin="normal"
            />

          </DialogContent>
          <DialogActions>
            <Button disabled={isExtending} onClick={handleCloseDialog}>Cancel</Button>
            <Button disabled={isExtending || !isValid} variant="contained" color="primary" onClick={handleExtendClick}>
              {isExtending ? 'Extending...' : 'Extend'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <Snackbar
        open={hasExtended}
        autoHideDuration={6000}
        message="Lock date extended"
        onClose={handleCloseSnackbar}
      />
    </>
  );
};

ExtendButton.propTypes = {
  invoice: PropTypes.object.isRequired,
  accountId: PropTypes.number.isRequired,
  onExtend: PropTypes.func.isRequired,
};

const styles = (theme) => ({
  extendButton: {
    marginLeft: theme.spacing(1),
    marginTop: -theme.spacing(0.5),
  },
});

export default withStyles(styles)(ExtendButton);
